import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Stack, TextField, Typography} from '@mui/material';
import {updateDocumentTitle} from '../../firestore/documents';
import {
    applyTitleToReportStore,
    TITLE_FIELD_ORIGIN,
} from '../../../../utils/syncReportDocumentTitle';

/**
 * Title field component with debounced Firestore persistence and yDoc sync.
 * Reads initial value and remote changes from yDocMeta (Y.Map).
 *
 * @param {Object} props
 * @param {string} props.documentId - Firestore document ID (persistence only)
 * @param {Function} props.onChange - Optional callback when title changes locally
 * @param {Object} props.yDocMeta - Y.Map reference for collaborative header metadata (source of truth)
 * @param {Object} props.ydoc - Y.Doc reference for creating transactions
 */
export default function TitleField({documentId, onChange, yDocMeta, ydoc, headerMetaTick = 0}) {
    const [title, setTitle] = useState(() => yDocMeta?.get('title') ?? '');
    const titleUpdateTimeoutRef = useRef(null);

    // Seed from yDocMeta when it becomes available
    useEffect(() => {
        if (!yDocMeta) return;
        const stored = yDocMeta.get('title');
        if (stored !== undefined) setTitle(stored);
    }, [yDocMeta, headerMetaTick]);

    // Listen for remote title changes in yDoc
    useEffect(() => {
        if (!yDocMeta) return;

        const observer = (event, transaction) => {
            // Own persist already updated local state + the report store.
            // Apply other local origins (header title-sync) and remote collab writes.
            if (transaction.origin === TITLE_FIELD_ORIGIN) return;
            if (event.changes.keys.has('title')) {
                const newTitle = yDocMeta.get('title');
                if (newTitle !== undefined) {
                    setTitle(newTitle);
                    applyTitleToReportStore(newTitle);
                }
            }
        };

        yDocMeta.observe(observer);
        return () => yDocMeta.unobserve(observer);
    }, [yDocMeta]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (titleUpdateTimeoutRef.current) {
                clearTimeout(titleUpdateTimeoutRef.current);
            }
        };
    }, []);

    const handleChange = useCallback((e) => {
        const newTitle = e.target.value;

        // Update local state immediately for responsive UI
        setTitle(newTitle);
        applyTitleToReportStore(newTitle);

        if (onChange) {
            onChange(newTitle);
        }

        if (titleUpdateTimeoutRef.current) {
            clearTimeout(titleUpdateTimeoutRef.current);
        }

        // Debounce writes after 2 seconds of inactivity
        titleUpdateTimeoutRef.current = setTimeout(async () => {
            // 1. Write to yDoc (real-time collaboration)
            if (yDocMeta && ydoc) {
                ydoc.transact(() => {
                    yDocMeta.set('title', newTitle);
                }, TITLE_FIELD_ORIGIN);
                console.log('[TitleField] Updated yDoc meta with title:', newTitle);
            }

            // 2. Persist to Firestore
            if (documentId) {
                try {
                    await updateDocumentTitle(documentId, newTitle);
                    console.log('[TitleField] Persisted title to Firestore');
                } catch (error) {
                    console.error('[TitleField] Error persisting title:', error);
                }
            }
        }, 2000);
    }, [documentId, onChange, yDocMeta, ydoc]);

    return (
        <Stack spacing={1}>
            <Typography color="text.secondary" sx={{opacity: 0.5}}>
                Document Title
            </Typography>
            <TextField
                fullWidth
                variant="standard"
                value={title}
                onChange={handleChange}
                placeholder="Document Title"
                autoComplete={'off'}
                multiline={true}
                InputProps={{
                    disableUnderline: true,
                }}
                sx={{
                    '& .MuiInputBase-root': {
                        padding: 0,
                        backgroundColor: 'transparent',
                    },
                    '& .MuiInputBase-input': {
                        padding: 0,
                        fontSize: '1.5rem',
                        lineHeight: '2rem',
                        fontWeight: 600,
                        backgroundColor: 'transparent',
                    },
                    '& .MuiInputBase-inputMultiline': {
                        padding: 0,
                    },
                    '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
                        display: 'none',
                    },
                    '& .MuiInputBase-root:hover:not(.Mui-disabled, .Mui-error):before': {
                        display: 'none',
                    }
                }}
            />
        </Stack>
    );
}
